import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  Select,
  Switch,
  Typography,
  notification,
} from 'antd';
import type { FormInstance } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Divider } from '@/shared/ui/Divider';
import type { Settings } from '@/models/settings';
import {
  useGetSessionDurationOptionsQuery,
  useGetInactivityTimeoutOptionsQuery,
} from '@/state/slices/options';
import {
  useRemoveTrustedDeviceMutation,
  useRegisterTrustedDeviceMutation,
} from '@/state/slices/auth';
import { getDeviceId } from '@/shared/utils/deviceId';
import {
  getTrustedDeviceUserKey,
  isDeviceTrustedLocally,
  setDeviceTrustedLocally,
} from '@/shared/utils/trustedDeviceStore';
import { UserContext } from '@/state/contexts';
import styles from './SecuritySection.module.css';

const { Text } = Typography;

const SESSION_DURATION_LABELS: Record<string, string> = {
  '24_HOURS': '24 hours',
  '48_HOURS': '48 hours',
  '1_WEEK': '1 week',
  '1_MONTH': '1 month',
  OFF: 'Off (no expiry)',
};

const INACTIVITY_TIMEOUT_LABELS: Record<string, string> = {
  '15_MINUTES': '15 minutes',
  '30_MINUTES': '30 minutes',
  '1_HOUR': '1 hour',
  '2_HOURS': '2 hours',
  OFF: 'Off (no timeout)',
};

interface SecuritySectionProps {
  pwForm: FormInstance;
  changingPassword: boolean;
  onChangePassword: () => void;
  settings: Settings;
  onUpdate: (partial: Partial<Settings>) => void;
}

export const SecuritySection = ({
  pwForm,
  changingPassword,
  onChangePassword,
  settings,
  onUpdate,
}: SecuritySectionProps) => {
  const { user } = useContext(UserContext);
  const { data: sessionDurationOptions } = useGetSessionDurationOptionsQuery();
  const { data: inactivityTimeoutOptions } =
    useGetInactivityTimeoutOptionsQuery();
  const [registerTrustedDevice] = useRegisterTrustedDeviceMutation();
  const [removeTrustedDevice] = useRemoveTrustedDeviceMutation();
  const [localDeviceId, setLocalDeviceId] = useState<string | null>(null);
  const userId = getTrustedDeviceUserKey(user?.id, user?.email, settings.user);

  const [trusted, setTrusted] = useState(() => isDeviceTrustedLocally(userId));

  useEffect(() => {
    getDeviceId().then(setLocalDeviceId);
  }, []);

  useEffect(() => {
    if (userId) setTrusted(isDeviceTrustedLocally(userId));
  }, [userId]);

  const handleRememberDeviceChange = (checked: boolean) => {
    if (!localDeviceId || !userId) return;

    setTrusted(checked);
    setDeviceTrustedLocally(userId, checked);

    if (checked) {
      queueMicrotask(async () => {
        try {
          await registerTrustedDevice({ deviceId: localDeviceId }).unwrap();
        } catch {
          setTrusted(false);
          setDeviceTrustedLocally(userId, false);
          notification.error({
            message: 'Could not register this device',
            description:
              'Sign out and sign in with MFA once, or try again in a moment.',
          });
        }
      });
    } else {
      queueMicrotask(async () => {
        try {
          await removeTrustedDevice({ deviceId: localDeviceId }).unwrap();
        } catch {
          setTrusted(true);
          setDeviceTrustedLocally(userId, true);
          notification.error({
            message: 'Could not remove this device',
            description:
              'Your account may still trust this browser until the server removes it.',
          });
        }
      });
    }
  };

  return (
    <div className={styles.rows}>
      <div className={styles.subsection}>
        <Text strong className={styles.subsectionTitle}>
          Password
        </Text>
        <Text type="secondary" className={styles.hint}>
          Update your password to keep your account secure.
        </Text>
      </div>

      <Form
        id="change-password"
        form={pwForm}
        layout="vertical"
        disabled={changingPassword}
        className={styles.form}
      >
        <Form.Item
          name="currentPassword"
          label="Current password"
          rules={[{ required: true, message: 'Required.' }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="New password"
          rules={[
            { required: true, message: 'Required.' },
            { min: 8, message: 'Minimum 8 characters.' },
          ]}
          hasFeedback
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirmNewPassword"
          label="Confirm new password"
          dependencies={['newPassword']}
          hasFeedback
          rules={[
            { required: true, message: 'Required.' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value)
                  return Promise.resolve();
                return Promise.reject(new Error('Passwords do not match.'));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Button
          type="primary"
          onClick={onChangePassword}
          loading={changingPassword}
        >
          Change password
        </Button>
      </Form>

      <Divider />

      <div id="two-factor-authentication" className={styles.subsection}>
        <Text strong className={styles.subsectionTitle}>
          Two-Factor Authentication
        </Text>
        <Text type="secondary" className={styles.hint}>
          <CheckCircleOutlined className={styles.mfaIcon} /> Your account is
          protected with two-factor authentication.
        </Text>
      </div>

      <Divider />

      <div className={styles.subsection}>
        <Text strong className={styles.subsectionTitle}>
          Session Security
        </Text>
        <Text type="secondary" className={styles.hint}>
          Control session behavior and device trust settings.
        </Text>
      </div>

      <Flex
        id="remember-this-device"
        align="center"
        justify="space-between"
        className={styles.row}
      >
        <Flex vertical>
          <Text>Remember this device</Text>
          <Text type="secondary" className={styles.hint}>
            When on, you can sign in on this device without entering a
            verification code.
          </Text>
        </Flex>
        <Switch
          checked={trusted}
          disabled={localDeviceId === null}
          onChange={handleRememberDeviceChange}
        />
      </Flex>

      <Divider />

      <Flex
        id="session-duration"
        align="center"
        justify="space-between"
        className={styles.row}
      >
        <Flex vertical>
          <Text>Session duration</Text>
          <Text type="secondary" className={styles.hint}>
            How long you stay logged in after signing in.
          </Text>
        </Flex>
        <Select
          className={styles.select}
          value={settings.sessionDuration}
          onChange={(value) => onUpdate({ sessionDuration: value })}
          options={(sessionDurationOptions ?? []).map((opt) => ({
            value: opt,
            label: SESSION_DURATION_LABELS[opt] ?? opt,
          }))}
        />
      </Flex>

      <Divider />

      <Flex
        id="inactivity-timeout"
        align="center"
        justify="space-between"
        className={styles.row}
      >
        <Flex vertical>
          <Text>Inactivity timeout</Text>
          <Text type="secondary" className={styles.hint}>
            Automatically log out after this period of inactivity.
          </Text>
        </Flex>
        <Select
          className={styles.select}
          value={settings.inactivityTimeout}
          onChange={(value) => onUpdate({ inactivityTimeout: value })}
          options={(inactivityTimeoutOptions ?? []).map((opt) => ({
            value: opt,
            label: INACTIVITY_TIMEOUT_LABELS[opt] ?? opt,
          }))}
        />
      </Flex>
    </div>
  );
};
